import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isTokenNearExpiry, refreshJwt } from '../../../../api'
import { JWTResponse } from '../../../../domain/domain'
import {
  getUserInfo, googleAuth, facebookAuth, phoneAuth, signIn, signUp, updateUserInfo
} from '../../../../pages/auth/api'
import {
  setExpiresInSeconds, setJWT, setLocalUser, setRefreshToken
} from '../../../../storage/local.storage'
import type {
  ApiException,
  AuthUserResponse,
  LoginUserRequest,
  ShiedSocialCredsType,
  ShieldRegisterType, UpdateUserPropType, User
} from '../../../../types'
import { AsyncState, retrieveErrorResponse } from '../../../util'
import { startLoading, stopLoading } from '../loader/loader.slice'

export const getUser = createAsyncThunk('user/info', getUserInfo)

const postInfoActions = (userInfo: AuthUserResponse) => {
  // Save the user in local storage
  setLocalUser(userInfo);
}

const postRegisterActions = async (
  tokenResponse: any,
  dispatch: any,
  cb?: (userInfo: User) => void,
  errorCb?: (error: any) => void
) => {
  if (!tokenResponse || !tokenResponse.token) {
    if (typeof errorCb === 'function') {
      errorCb(tokenResponse)
    }

    dispatch(stopLoading())
    return null
  }

  // Save the token in local storage
  setJWT(tokenResponse?.token)
  setRefreshToken(tokenResponse?.refreshToken)
  setExpiresInSeconds(tokenResponse?.expiresIn)
  // Get the user details from backend
  // const userInfo = await register()
  // return await postInfoActions(userInfo, dispatch, cb, errorCb)
}

const postLoginActions = (jwt: JWTResponse) => {
  // Save the token in local storage
  setJWT(jwt?.accessToken);
  setRefreshToken(jwt?.refreshToken);
  setExpiresInSeconds(jwt?.expiresIn);
}

const postUpdateInfoActions = async (
  tokenResponse: any,
  dispatch: any,
  cb?: (userInfo: User) => void,
  errorCb?: (error: any) => void
) => {
  if (!tokenResponse || !tokenResponse.token) {
    if (typeof errorCb === 'function') {
      errorCb(tokenResponse)
    }

    dispatch(stopLoading())
    return null
  }

  // const userInfo = await getUserInfo()
  // return await postInfoActions(userInfo, dispatch, cb, errorCb)
}

export const socialAuthAction = createAsyncThunk('user/google', async (config: [
  provider: 'facebook' | 'google' | 'phone' | 'github',
  data: ShiedSocialCredsType,
  cb?: (userInfo: User) => void,
  errorCb?: (error: any) => void,
], thunk) => {
  const { dispatch } = thunk
  const [
    provider, data, cb, errorCb
  ] = config
  // Dispatch app loader
  dispatch(startLoading({ text: 'logging-you-in' }))
  let tokenResponse
  switch (provider) {
    case 'facebook':
      tokenResponse = await facebookAuth(data)
      break
    case 'google':
      tokenResponse = await googleAuth(data)
      break
    case 'phone':
      tokenResponse = await phoneAuth(data)
      break
    default:
      return null
  }

  if (tokenResponse.initUser) {
    return await postRegisterActions(tokenResponse, dispatch, cb, errorCb)
  }

  // return await postLoginActions(tokenResponse, dispatch, cb, errorCb)
})
export const loginUserAction = createAsyncThunk('user/login', async (creds: LoginUserRequest, thunk) => {
  const { dispatch } = thunk;
  // Dispatch app loader
  dispatch(startLoading({ text: 'logging-you-in' }));
  try {
    const authResponse = await signIn(creds);
    postLoginActions(authResponse?.data?.content?.jwt);
    postInfoActions(authResponse?.data?.content);
    dispatch(stopLoading());
    return authResponse?.data?.content;
  } catch (e) {
    dispatch(stopLoading());
    return thunk.rejectWithValue(retrieveErrorResponse(e));
  }
})

export const registerUserAction = createAsyncThunk('user/register', async (config: [
  data: ShieldRegisterType, cb?: (userInfo: User) => void, errorCb?: (error: any) => void,
], thunk) => {
  const { dispatch } = thunk
  const [data, cb, errorCb] = config
  // Dispatch app loader
  dispatch(startLoading({ text: 'signing-you-up' }))
  const tokenResponse = await signUp(data)
  return await postRegisterActions(tokenResponse, dispatch, cb, errorCb)
})

export const loadUserAction = createAsyncThunk('user/load', async () => {
  // Check if token has expired
  if (isTokenNearExpiry()) {
    await refreshJwt()
  }

  const userInfo = await getUserInfo()
  return userInfo.content
})

export const updateUserInfoAction = createAsyncThunk('user/details', async (config: [
  data: UpdateUserPropType, cb?: (userInfo: User) => void, errorCb?: (error: any) => void,
], thunk) => {
  const { dispatch } = thunk
  const [data, cb, errorCb] = config
  // Dispatch app loader
  dispatch(startLoading({ text: 'updating-your-info' }))
  const tokenResponse = await updateUserInfo(data)
  return await postUpdateInfoActions(tokenResponse, dispatch, cb, errorCb)
});

const initialState: AsyncState<AuthUserResponse, ApiException> = {
  loading: false
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers (builder) {
    builder.addCase(loginUserAction.pending, (state) => {
      state.loading = true;
      state.errors = undefined;
    }).addCase(loginUserAction.fulfilled, (state, action) => {
      state.loading = false;
      state.payload = action.payload;
      state.errors = undefined;
    }).addCase(loginUserAction.rejected, (state, action) => {
      state.loading = false;
      state.errors = action.payload as ApiException[];
    })
  }
})

export default userSlice.reducer
